import { Dialog } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"

import type { AddedProduct, NewRequestFormValues, SalesCustomer, SalesRequester } from "../../types"
import { NewRequestStep } from "../../types"

import { CustomerSearchStep } from "./CustomerSearchStep"
import { ProductSearchStep } from "./ProductSearchStep"
import { RequesterAndLocationsStep } from "./RequesterAndLocationsStep"

import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { Location } from "@/graphql/codegen/graphql"

interface NewRequestDialogProps {
  fullScreen?: boolean
  onClose: () => void
}

export const NewRequestDialog: FC<NewRequestDialogProps> = ({ fullScreen = false, onClose }) => {
  const [step, setStep] = useState(NewRequestStep.Customer)
  const [formValues, setFormValues] = useState<Partial<NewRequestFormValues>>({})
  const [isConfirmCancelOpen, setIsConfirmCancelOpen] = useState(false)

  const handleCancel = () => setIsConfirmCancelOpen(true)

  const handleCustomerContinue = (customer: SalesCustomer) => {
    setFormValues({ customer })
    setStep(NewRequestStep.RequesterAndLocations)
  }

  const handleRequesterAndLocationsContinue = (requesters: SalesRequester[], locations: Location[]) => {
    setFormValues((prev) => ({ ...prev, requesters, locations }))
    setStep(NewRequestStep.Product)
  }

  const handleProductsContinue = (products: AddedProduct[]) => {
    setFormValues((prev) => ({ ...prev, products }))
  }

  return (
    <Dialog
      open
      onClose={handleCancel}
      fullScreen={fullScreen}
      maxWidth='sm'
      fullWidth
      data-testid='sales.new-request-dialog'
    >
      {isConfirmCancelOpen ? (
        <CancelDialog onClose={() => setIsConfirmCancelOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogHeader title='New Request' onClose={handleCancel} />

          {step === NewRequestStep.Customer && (
            <CustomerSearchStep onCancel={handleCancel} onContinue={handleCustomerContinue} />
          )}

          {step === NewRequestStep.RequesterAndLocations && formValues.customer && (
            <RequesterAndLocationsStep
              customer={formValues.customer}
              onCancel={handleCancel}
              onContinue={handleRequesterAndLocationsContinue}
            />
          )}

          {step === NewRequestStep.Product && formValues.customer && formValues.requesters && formValues.locations && (
            <ProductSearchStep
              formValues={formValues as NewRequestFormValues}
              onCancel={handleCancel}
              onContinue={handleProductsContinue}
            />
          )}
        </>
      )}
    </Dialog>
  )
}
